import { useEffect } from "react";

import { useRouter } from "next/router";

import Cookies from "js-cookie";

import { useDisclosure } from "@/storybook-components";
import { useAppSelector, useAppDispatch } from "@/redux/store/hooks";
import useUser from "@/hooks/useUser";
import ActionButton from "./action-button";

import { getVotingStatus } from "@/helpers/ideas";
import useUnverifiedModal from "@/hooks/useUnverfiedModal";
import {
  setTriggerPoint,
  setModalStatus
} from "@/redux/features/unverified-modal/slice";

import OpenPostDrawer from "../open-post-drawer";

const PostDrawer = (): JSX.Element => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { brainstormer } = useAppSelector((state) => state?.brainstormers);
  const { triggerPoint, clickedButtonStatus } = useAppSelector(
    (state) => state.unverifiedModal
  );
  const cookieValues = Cookies.get("trigger_point");
  let cookieTriggerPoint;
  if (cookieValues) {
    cookieTriggerPoint = JSON.parse(cookieValues);
  }
  const {
    allowUnverifiedParticipation,
    state: brainStormerState,
    votingStartAt,
    votingEndAt
  } = brainstormer;

  const isVotingAllowed =
    (votingStartAt &&
      votingEndAt &&
      getVotingStatus(votingStartAt, votingEndAt) === "active") ||
    getVotingStatus(votingStartAt, votingEndAt) === "ended";

  const {
    canContribute,
    visibilityMode,
    state: projectState
  } = useAppSelector((state) => state.project.project);
  const { isUnverfiedUserLoggedIn, isRegisteredUserLoggedIn } = useUser();

  const routerPath = router.asPath;
  const { action } = router.query;
  const createPostDisclosure = useDisclosure();

  // to trigger the unverified modal
  useUnverifiedModal(allowUnverifiedParticipation, (isProceedAllowed) => {
    if (isProceedAllowed && clickedButtonStatus === "create") {
      dispatch(
        setTriggerPoint({ ...triggerPoint, isTriggeredPointActive: false })
      );
      if (
        triggerPoint.triggeredSource === "new_idea" ||
        cookieTriggerPoint?.triggeredSource === "new_idea"
      ) {
        createPostDisclosure.onOpen();
        addQueryParam();
      }
      Cookies.remove("trigger_point");
    }
  });
  const addQueryParam = () => {
    router.query.action = "new_idea";
    router.replace(router, "", { shallow: true });
  };

  const removeQueryParam = () => {
    const [updatedPath] = routerPath.split("?");
    router.replace(updatedPath, undefined, { shallow: true });
  };

  const triggerModal = async () => {
    await removeQueryParam();
    const triggerPoint = {
      triggeredSource: "new_idea",
      isTriggeredPointActive: true
    };
    Cookies.set("trigger_point", JSON.stringify(triggerPoint), {
      expires: 1,
      secure: true,
      sameSite: "Strict"
    });
    await dispatch(setTriggerPoint(triggerPoint));
  };

  useEffect(() => {
    if (action === "new_idea") {
      if (visibilityMode === "Panelled" && !canContribute) {
        removeQueryParam();
        return;
      }
      if (brainStormerState === "archived" || projectState === "archived") {
        removeQueryParam();
        return;
      }
      if (
        getVotingStatus(votingStartAt, votingEndAt) === "ended" ||
        getVotingStatus(votingStartAt, votingEndAt) === "active"
      ) {
        removeQueryParam();
        return;
      }
      if (!isUnverfiedUserLoggedIn || !isRegisteredUserLoggedIn) {
        triggerModal();
        return;
      }
      createPostDisclosure.onOpen();
    }
  }, [
    createPostDisclosure.onOpen,
    action,
    visibilityMode,
    canContribute,
    brainStormerState,
    projectState,
    votingStartAt,
    votingEndAt
  ]);

  return (
    <>
      {projectState !== "archived" && brainStormerState !== "archived" && (
        <ActionButton
          projectVisibility={visibilityMode}
          canContribute={canContribute}
          isVotingAllowed={isVotingAllowed}
          allowUnverifiedParticipation={allowUnverifiedParticipation}
          onOpenHandler={createPostDisclosure.onOpen}
        />
      )}
      {(isUnverfiedUserLoggedIn || isRegisteredUserLoggedIn) &&
        createPostDisclosure.isOpen && (
          <OpenPostDrawer
            onOpen={createPostDisclosure.onOpen}
            isOpen={createPostDisclosure.isOpen}
            onClose={() => {
              dispatch(setModalStatus("none"));
              createPostDisclosure.onClose();
            }}
            type="create"
            isEditPost={false}
          />
        )}
    </>
  );
};

export default PostDrawer;
